import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { FiCalendar, FiList, FiPlus, FiTrash2, FiEdit2, FiClock, FiUser, FiPhone, FiMapPin, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { generateTimeSlots, isSlotBooked } from '../../utils/appointmentUtils';
import '../../styles/BookingsManager.css';

const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const BookingsManager = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingBooking, setEditingBooking] = useState(null);
    const [bookedSlotsMap, setBookedSlotsMap] = useState({});

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        city: '',
        serviceType: '',
        date: formatDateString(new Date()),
        timeSlot: '',
        message: ''
    });

    useEffect(() => {
        const q = query(collection(db, "appointments"), orderBy("date", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const bookingsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setBookings(bookingsList);

            // Map booked slots for easy lookup
            const slots = {};
            bookingsList.forEach(b => {
                const key = `${b.date}_${b.serviceType}_${b.timeSlot}`;
                slots[key] = true;
            });
            setBookedSlotsMap(slots);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this booking?')) {
            try {
                await deleteDoc(doc(db, "appointments", id));
            } catch (error) {
                console.error("Error deleting booking: ", error);
                alert("Failed to delete booking");
            }
        }
    };

    const handleAddBooking = async (e) => {
        e.preventDefault();
        try {
            if (isSlotBooked(formData.date, formData.serviceType, formData.timeSlot, bookedSlotsMap)) {
                alert('This slot is already booked!');
                return;
            }

            await addDoc(collection(db, "appointments"), {
                ...formData,
                createdAt: new Date().toISOString(),
                source: 'Manual'
            });
            setShowAddModal(false);
            setFormData({
                name: '', phone: '', email: '', city: '',
                serviceType: '', date: formatDateString(new Date()),
                timeSlot: '', message: ''
            });
            alert('Booking added successfully!');
        } catch (error) {
            console.error("Error adding booking: ", error);
            alert("Failed to add booking");
        }
    };

    const handleEditBooking = (booking) => {
        setEditingBooking(booking);
        setFormData({
            name: booking.name || '',
            phone: booking.phone || '',
            email: booking.email || '',
            city: booking.city || '',
            serviceType: booking.serviceType || '',
            date: booking.date || formatDateString(new Date()),
            timeSlot: booking.timeSlot || '',
            message: booking.message || ''
        });
        setShowEditModal(true);
    };

    const handleUpdateBooking = async (e) => {
        e.preventDefault();
        try {
            const { id } = editingBooking;

            // If the slot or date changed, check if the new slot is available
            const slotChanged = formData.date !== editingBooking.date ||
                formData.timeSlot !== editingBooking.timeSlot ||
                formData.serviceType !== editingBooking.serviceType;

            if (slotChanged && isSlotBooked(formData.date, formData.serviceType, formData.timeSlot, bookedSlotsMap)) {
                alert('This new slot is already booked!');
                return;
            }

            const appointmentRef = doc(db, "appointments", id);
            await updateDoc(appointmentRef, {
                ...formData,
                updatedAt: new Date().toISOString()
            });

            setShowEditModal(false);
            setEditingBooking(null);
            setFormData({
                name: '', phone: '', email: '', city: '',
                serviceType: '', date: formatDateString(new Date()),
                timeSlot: '', message: ''
            });
            alert('Booking updated successfully!');
        } catch (error) {
            console.error("Error updating booking: ", error);
            alert("Failed to update booking");
        }
    };

    const filteredBookings = bookings.filter(b => b.date === formatDateString(selectedDate));

    const getBookingsCountForDate = (date) => {
        const dateStr = formatDateString(date);
        return bookings.filter(b => b.date === dateStr).length;
    };

    const timeSlots = generateTimeSlots(formData.serviceType);

    if (loading) return <div className="admin-loading"><div className="spinner"></div></div>;

    return (
        <div className="bookings-manager">
            <div className="manager-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <h2 style={{ margin: 0 }}>Booking Management</h2>
                    <div className="view-switcher">
                        <button
                            className={viewMode === 'calendar' ? 'active' : ''}
                            onClick={() => setViewMode('calendar')}
                        >
                            <FiCalendar /> Calendar
                        </button>
                        <button
                            className={viewMode === 'list' ? 'active' : ''}
                            onClick={() => setViewMode('list')}
                        >
                            <FiList /> All Bookings
                        </button>
                    </div>
                </div>
                <button className="btn-primary" onClick={() => setShowAddModal(true)}>
                    <FiPlus /> New Manual Booking
                </button>
            </div>

            {viewMode === 'calendar' ? (
                <div className="calendar-view-container">
                    <div className="calendar-sidebar">
                        <Calendar
                            onChange={setSelectedDate}
                            value={selectedDate}
                            tileContent={({ date, view }) => {
                                if (view === 'month') {
                                    const count = getBookingsCountForDate(date);
                                    return count > 0 ? <div className="dot-badge">{count}</div> : null;
                                }
                            }}
                        />
                    </div>
                    <div className="date-details">
                        <div className="date-header">
                            <h3>Bookings for {selectedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
                            <span className="count-badge">{filteredBookings.length} Bookings</span>
                        </div>
                        {filteredBookings.length === 0 ? (
                            <div className="empty-state">
                                <p>No bookings for this date.</p>
                            </div>
                        ) : (
                            <div className="bookings-list-mini">
                                {filteredBookings.map(booking => (
                                    <div key={booking.id} className="booking-item-mini">
                                        <div className="booking-info">
                                            <div className="time-badge"><FiClock /> {booking.timeSlot}</div>
                                            <div className="patient-name"><FiUser /> {booking.name}</div>
                                            <div className="service-type">{booking.serviceType}</div>
                                        </div>
                                        <div className="booking-actions" style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={() => handleEditBooking(booking)} className="btn-edit-small"><FiEdit2 /></button>
                                            <button onClick={() => handleDelete(booking.id)} className="btn-delete-small"><FiTrash2 /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="list-view-container">
                    <div className="bookings-table-wrapper">
                        <table className="bookings-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Patient</th>
                                    <th>Service</th>
                                    <th>Source</th>
                                    <th>Contact</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings.map(booking => (
                                    <tr key={booking.id}>
                                        <td>{booking.date}</td>
                                        <td>{booking.timeSlot}</td>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{booking.name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>{booking.city}</div>
                                        </td>
                                        <td>{booking.serviceType}</td>
                                        <td>
                                            <span className={`badge ${booking.source === 'Manual' ? 'badge-manual' : 'badge-website'}`}>
                                                {booking.source || 'Website'}
                                            </span>
                                        </td>
                                        <td>
                                            <div><FiPhone size={12} /> {booking.phone}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>{booking.email}</div>
                                        </td>
                                        <td>
                                            <button onClick={() => handleDelete(booking.id)} className="btn-icon btn-delete"><FiTrash2 /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Add/Edit Booking Modal */}
            {(showAddModal || showEditModal) && (
                <div className="modal-overlay">
                    <div className="admin-modal">
                        <div className="modal-header">
                            <div>
                                <h3 style={{ margin: 0 }}>{showEditModal ? 'Edit Appointment' : 'Add New Appointment'}</h3>
                                <p style={{ margin: '5px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                                    {showEditModal ? 'Modify the details for this patient booking' : 'Enter the details for manual patient booking'}
                                </p>
                            </div>
                            <button onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditingBooking(null); }} className="close-btn"><FiXCircle /></button>
                        </div>

                        <div className="admin-modal-form">
                            <form onSubmit={showEditModal ? handleUpdateBooking : handleAddBooking}>
                                <div className="form-section-title">Patient Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label><FiUser size={14} /> Patient Name*</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Full Name"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><FiPhone size={14} /> Phone Number*</label>
                                        <input
                                            type="tel"
                                            required
                                            placeholder="10-digit mobile"
                                            value={formData.phone}
                                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Email ID</label>
                                        <input
                                            type="email"
                                            placeholder="example@mail.com"
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><FiMapPin size={14} /> City</label>
                                        <input
                                            type="text"
                                            placeholder="Patient's City"
                                            value={formData.city}
                                            onChange={e => setFormData({ ...formData, city: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-section-title">Appointment Information</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Service Type*</label>
                                        <select
                                            required
                                            value={formData.serviceType}
                                            onChange={e => setFormData({ ...formData, serviceType: e.target.value, timeSlot: '' })}
                                        >
                                            <option value="">Select Service</option>
                                            <option value="Cosmetic Gynaecology">Cosmetic Gynaecology</option>
                                            <option value="Cosmetic Dermatology">Cosmetic Dermatology</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label><FiCalendar size={14} /> Appointment Date*</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.date}
                                            onChange={e => setFormData({ ...formData, date: e.target.value, timeSlot: '' })}
                                        />
                                    </div>
                                </div>

                                <div className="form-section-title">Appointment Timing</div>
                                <div className="time-slot-selection">
                                    {!formData.serviceType || !formData.date ? (
                                        <div style={{ padding: '20px', textAlign: 'center', background: '#f8fafc', border: '1.5px dashed #cbd5e1', borderRadius: '16px', color: '#64748b' }}>
                                            <FiClock size={24} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.5 }} />
                                            <p style={{ margin: 0, fontSize: '0.9rem' }}>Please select <b>Service Type</b> and <b>Date</b> above to view available timing slots.</p>
                                        </div>
                                    ) : (
                                        <div className="form-group">
                                            <label><FiClock size={14} /> Available Slots for {formData.serviceType}*</label>
                                            <div className="slots-grid">
                                                {timeSlots.map(slot => {
                                                    const isSelected = formData.timeSlot === slot.value;
                                                    const isBooked = isSlotBooked(formData.date, formData.serviceType, slot.value, bookedSlotsMap);

                                                    // Allow current slot if we are editing
                                                    const isCurrentlyBookedByMe = showEditModal &&
                                                        formData.date === editingBooking.date &&
                                                        slot.value === editingBooking.timeSlot &&
                                                        formData.serviceType === editingBooking.serviceType;

                                                    return (
                                                        <button
                                                            key={slot.value}
                                                            type="button"
                                                            disabled={isBooked && !isCurrentlyBookedByMe}
                                                            className={`slot-btn ${isSelected ? 'selected' : ''} ${isBooked && !isCurrentlyBookedByMe ? 'booked' : ''}`}
                                                            onClick={() => setFormData({ ...formData, timeSlot: slot.value })}
                                                        >
                                                            <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>{slot.display.split(' to ')[0]}</span>
                                                            <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                                                                {isBooked && !isCurrentlyBookedByMe ? 'Booked' : (isSelected ? 'Selected' : 'Available')}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Message / Notes</label>
                                    <textarea
                                        rows="3"
                                        placeholder="Any specific concerns or medical notes..."
                                        value={formData.message}
                                        onChange={e => setFormData({ ...formData, message: e.target.value })}
                                    ></textarea>
                                </div>
                            </form>
                        </div>

                        <div className="modal-actions">
                            <button type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditingBooking(null); }} className="btn-cancel">Cancel</button>
                            <button
                                type="button"
                                onClick={showEditModal ? handleUpdateBooking : handleAddBooking}
                                className="btn-confirm"
                                disabled={!formData.timeSlot || !formData.name || !formData.phone}
                                style={{
                                    opacity: (!formData.timeSlot || !formData.name || !formData.phone) ? 0.6 : 1,
                                    cursor: (!formData.timeSlot || !formData.name || !formData.phone) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <FiCheckCircle style={{ marginRight: '8px' }} /> {showEditModal ? 'Update Appointment' : 'Confirm Appointment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingsManager;
